import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCategories,
  useCreateMentor,
  useDeactivateMentor,
  useMentors,
  useReactivateMentor,
  useResetMentorPassword,
  useUpdateMentor,
} from '@/lib/queries'
import type { MentorFilter } from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal, ConfirmDialog } from '@/components/ui/Modal'
import {
  MultiSelectField,
  SelectField,
  TextField,
} from '@/components/ui/Field'
import { Pill, UserStatusPill, VisibilityPill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatRating, initials } from '@/lib/format'
import type { Mentor, MentorCreated, MentorUpdateInput } from '@/types/api'
import { CredentialDialog } from './CredentialDialog'
import { MentorBreaksCard } from './MentorBreaksCard'
import { RatingOverrideDialog } from './RatingOverrideDialog'
import styles from './MentorsPage.module.css'

/** Common zones, so the field is not a free-text trap. */
const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
]

/**
 * A8–A11 — Mentors.
 *
 * *** ADMIN CREATES MENTORS; THERE IS NO APPROVAL QUEUE ***
 * The mockups showed a "pending → approve" flow. That was superseded: an admin
 * issues credentials directly, and the mentor completes their own profile.
 *
 * *** THE VISIBILITY GATE IS THE THING TO EXPLAIN ***
 * A freshly created mentor is INVISIBLE to students until they finish their
 * profile — the admin cannot do it for them. Without saying so, "I created a
 * mentor and nobody can book them" becomes a support ticket every time.
 */
export function MentorsPage() {
  const toast = useToast()
  /*
    *** ACTIVE ONLY BY DEFAULT ***
    Deactivation exists largely for test mentors created inside a real
    client's tenant, so a list that still showed them would defeat its
    purpose. The admin has to ask to see them.
  */
  const [filter, setFilter] = useState<MentorFilter>('active')
  const { data, isLoading, error, refetch } = useMentors(filter)

  const deactivate = useDeactivateMentor()
  const reactivate = useReactivateMentor()

  /*
    The mentor awaiting a deactivation decision, plus the session count the
    server reported. `sessions === null` means we have not asked yet.
  */
  const [deactivating, setDeactivating] = useState<{
    mentor: Mentor
    sessions: number | null
  } | null>(null)
  const categories = useCategories()

  const createMutation = useCreateMentor()
  const updateMutation = useUpdateMentor()
  const resetMutation = useResetMentorPassword()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Mentor | null>(null)
  const [resetting, setResetting] = useState<Mentor | null>(null)
  const [overriding, setOverriding] = useState<Mentor | null>(null)
  const [search, setSearch] = useState('')

  // An edit that moves a mentor's sign-in, held until it is confirmed. The
  // whole payload is kept, not just the address — the confirmation happens
  // after the form is filled, and the rest of the edit must go with it.
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    mentor: Mentor
    input: MentorUpdateInput
    to: string
  } | null>(null)

  // Category ids for the form, held separately because the multi-select is a
  // controlled component rather than a native form field.
  const [draftCategories, setDraftCategories] = useState<string[]>([])

  /** Shown once, after create or reset. Never retrievable again. */
  const [credential, setCredential] = useState<MentorCreated | null>(null)

  const mentors = data?.mentors ?? []
  const categoryOptions = (categories.data?.categories ?? []).map((category) => ({
    value: category.category_id,
    label: category.name,
  }))
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const category of categories.data?.categories ?? []) {
      map.set(category.category_id, category.name)
    }
    return map
  }, [categories.data])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return mentors
    return mentors.filter(
      (mentor) =>
        mentor.full_name.toLowerCase().includes(query) ||
        mentor.email.toLowerCase().includes(query),
    )
  }, [mentors, search])

  const awaitingProfile = mentors.filter((mentor) => !mentor.profile_complete)

  const openCreate = () => {
    setDraftCategories([])
    setCreating(true)
  }

  const openEdit = (mentor: Mentor) => {
    setDraftCategories(mentor.category_ids)
    setEditing(mentor)
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
  }

  /*
    *** THE FIRST CALL IS A QUESTION, THE SECOND IS THE ANSWER ***
    A deactivated mentor cannot sign in, so they cannot join a session
    either — leaving their confirmed bookings in place would send students
    to a call nobody attends.

    So the server refuses with a 409 naming how many sessions are at stake.
    That count is what the admin confirms, and only then are they cancelled
    (quota returned, students notified).
  */
  const askToDeactivate = async (mentor: Mentor) => {
    try {
      const result = await deactivate.mutateAsync({
        id: mentor.user_id,
        cancelUpcoming: false,
      })
      // No upcoming sessions: nothing to confirm, it is already done.
      toast.success(`${mentor.full_name} has been deactivated.`)
      void result
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        // The server counted the sessions; show them before going further.
        const match = caught.message.match(/(\d+)/)
        setDeactivating({
          mentor,
          sessions: match ? Number(match[1]) : null,
        })
        return
      }
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not deactivate this mentor.',
      )
    }
  }

  const confirmDeactivate = async () => {
    if (!deactivating) return
    const { mentor } = deactivating
    try {
      const result = await deactivate.mutateAsync({
        id: mentor.user_id,
        cancelUpcoming: true,
      })
      setDeactivating(null)
      toast.success(
        result.sessions_cancelled > 0
          ? `${mentor.full_name} deactivated. ${result.sessions_cancelled} session` +
            `${result.sessions_cancelled === 1 ? '' : 's'} cancelled and ` +
            `those students notified.`
          : `${mentor.full_name} has been deactivated.`,
      )
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not deactivate this mentor.',
      )
    }
  }

  const handleReactivate = async (mentor: Mentor) => {
    try {
      await reactivate.mutateAsync(mentor.user_id)
      toast.success(
        `${mentor.full_name} can sign in again. They stay hidden from ` +
          `students until they make their profile visible.`,
      )
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reactivate this mentor.',
      )
    }
  }

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    if (draftCategories.length === 0) {
      toast.error('Choose at least one category, or students cannot find them.')
      return
    }

    try {
      const created = await createMutation.mutateAsync({
        full_name: String(form.get('full_name') ?? '').trim(),
        email: String(form.get('email') ?? '').trim(),
        phone: String(form.get('phone') ?? '').trim() || null,
        timezone: String(form.get('timezone') ?? 'Asia/Kolkata'),
        category_ids: draftCategories,
        notification_lead_minutes: Number(
          form.get('notification_lead_minutes') ?? 15,
        ),
      })
      closeForm()
      // Straight into the credential dialog: the password is on screen now or
      // never.
      setCredential(created)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not create the mentor.',
      )
    }
  }

  /**
   * Send the edit. Shared by the ordinary save and by the confirmed
   * email-change path, so both produce identical results and messaging.
   */
  const saveMentor = async (
    mentor: Mentor,
    input: MentorUpdateInput,
    movedTo: string | null,
  ) => {
    try {
      await updateMutation.mutateAsync({ id: mentor.user_id, input })
      toast.success(
        movedTo
          ? `${mentor.full_name} updated. They now sign in with ${movedTo}.`
          : `${mentor.full_name} updated.`,
      )
      setPendingEmailChange(null)
      closeForm()
    } catch (caught) {
      // Leave the form open on failure — a duplicate address is a correctable
      // mistake, and closing would discard the rest of the edit with it.
      setPendingEmailChange(null)
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save changes.',
      )
    }
  }

  const handleUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editing) return
    const form = new FormData(event.currentTarget)

    const email = String(form.get('email') ?? '').trim()
    const input: MentorUpdateInput = {
      full_name: String(form.get('full_name') ?? '').trim(),
      email,
      phone: String(form.get('phone') ?? '').trim() || null,
      timezone: String(form.get('timezone') ?? editing.timezone),
      category_ids: draftCategories,
      notification_lead_minutes: Number(
        form.get('notification_lead_minutes') ?? editing.notification_lead_minutes,
      ),
    }

    // *** THIS IS THE ONE EDIT THAT CAN LOCK SOMEBODY OUT ***
    // Every other field here is cosmetic; the email IS the mentor's sign-in.
    // An admin correcting a typo in the name should not be able to move an
    // account's credentials without noticing, so the change is confirmed
    // separately — and only when it actually changed.
    if (email !== '' && email !== editing.email) {
      setPendingEmailChange({ mentor: editing, input, to: email })
      return
    }

    await saveMentor(editing, input, null)
  }

  const handleReset = async () => {
    if (!resetting) return
    try {
      const result = await resetMutation.mutateAsync(resetting.user_id)
      setResetting(null)
      setCredential(result)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not reset the password.',
      )
      setResetting(null)
    }
  }

  const columns: Array<Column<Mentor>> = [
    {
      key: 'mentor',
      header: 'Mentor',
      render: (row) => (
        <div className={styles.mentorCell}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(row.full_name)}
          </span>
          <div className={styles.mentorText}>
            <span className={styles.mentorName}>{row.full_name}</span>
            <span className={styles.mentorEmail}>{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'categories',
      header: 'Categories',
      render: (row) =>
        row.category_ids.length === 0 ? (
          <Pill tone="warning">None</Pill>
        ) : (
          <div className={styles.categoryList}>
            {row.category_ids.map((id) => (
              <Pill key={id} tone="indigo">
                {categoryNames.get(id) ?? 'Unknown'}
              </Pill>
            ))}
          </div>
        ),
    },
    {
      key: 'visibility',
      header: 'Students can book',
      render: (row) => (
        <VisibilityPill
          isVisible={row.is_visible}
          profileComplete={row.profile_complete}
        />
      ),
    },
    {
      key: 'rating',
      header: 'Rating',
      align: 'right',
      secondary: true,
      render: (row) => (
        // Read-only again. The action lives with the other row actions, where
        // an admin already looks for things to do to a mentor — a number that
        // is secretly a button is not discoverable, and a pencil floating in a
        // numeric column reads as decoration.
        <span
          className={row.rating_is_overridden ? styles.ratingOverridden : undefined}
          title={
            row.rating_is_overridden && row.rating_override_reason
              ? row.rating_override_reason
              : undefined
          }
        >
          {row.rating_is_overridden || row.rating_count > 0 ? (
            <>
              <span className="numeric">
                {formatRating(row.rating_avg)}
                {!row.rating_is_overridden && (
                  <span className={styles.ratingCount}> ({row.rating_count})</span>
                )}
              </span>
              {/* An admin must be able to tell a real 4.5 from one they set
                  last month, without opening anything. */}
              {row.rating_is_overridden && (
                <span className={styles.ratingWas}>
                  set · was {formatRating(row.rating_true_avg) || '—'}
                </span>
              )}
            </>
          ) : (
            <span className={styles.muted}>—</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Account',
      secondary: true,
      render: (row) => <UserStatusPill status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // Ghost buttons; too narrow and they wrap into a ragged stack.
      width: '420px',
      render: (row) =>
        row.status === 'SUSPENDED' ? (
          /*
            A deactivated mentor gets ONE action. Editing categories or
            resetting a password for someone who cannot sign in is noise,
            and it invites an admin to think they have changed something.
          */
          <div className={styles.rowActions}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void handleReactivate(row)}
            >
              Reactivate
            </Button>
          </div>
        ) : (
          <div className={styles.rowActions}>
            <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOverriding(row)}>
              Edit display rating
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setResetting(row)}>
              Reset password
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void askToDeactivate(row)}
            >
              Deactivate
            </Button>
          </div>
        ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Mentors"
        description="Create accounts and assign categories. Mentors complete their own profiles before students can book them."
        actions={
          <Button variant="primary" onClick={openCreate}>
            Add mentor
          </Button>
        }
      />

      {awaitingProfile.length > 0 && (
        <Callout tone="warning" title="Waiting on mentors to finish setting up">
          <p>
            {awaitingProfile.length} mentor
            {awaitingProfile.length === 1 ? '' : 's'} —{' '}
            {awaitingProfile.map((mentor) => mentor.full_name).join(', ')} —
            {awaitingProfile.length === 1 ? ' has ' : ' have '}
            not completed their profile, so students cannot see or book them.
            They need to sign in, change their password, and fill in their title
            and bio.
          </p>
        </Callout>
      )}

      <Card flush>
        <div className={styles.tableToolbar}>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or email…"
            className={styles.search}
            aria-label="Search mentors"
          />
          {/*
            A plain select, not a Field: this sits in a toolbar beside the
            search box, and a labelled form control would tower over it.
          */}
          <select
            value={filter}
            onChange={(event) =>
              setFilter(event.target.value as MentorFilter)
            }
            className={styles.filter}
            aria-label="Which mentors to show"
          >
            <option value="active">Active mentors</option>
            <option value="deactivated">Deactivated mentors</option>
            <option value="all">All mentors</option>
          </select>
          <span className={styles.count}>
            {filtered.length} of {mentors.length}
          </span>
        </div>

        <Table
          columns={columns}
          rows={filtered}
          rowKey={(row) => row.user_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          isFiltered={search.trim().length > 0}
          emptyMessage="No mentors yet. Add one to start taking bookings."
          emptyFilteredMessage="No mentors match that search."
        />
      </Card>

      {/*
        Below the roster on purpose: "who are my mentors" comes first, and
        "when are they away" answers the follow-up question — why a mentor
        with a complete profile still has no bookable slots next week.
      */}
      <MentorBreaksCard />

      {/* ------------------------------------------------------- create */}
      <Modal
        open={creating}
        onClose={closeForm}
        title="Add mentor"
        description="A temporary password is generated. You will see it once."
      >
        <form onSubmit={handleCreate} className={styles.form}>
          <TextField
            label="Full name"
            name="full_name"
            required
            autoFocus
            maxLength={200}
          />

          <TextField
            label="Email"
            name="email"
            type="email"
            required
            hint="They sign in with this. Credentials are also emailed to them."
          />

          <div className={styles.formRow}>
            <TextField
              label="Phone"
              name="phone"
              type="tel"
              placeholder="+91…"
            />

            <SelectField
              label="Timezone"
              name="timezone"
              defaultValue="Asia/Kolkata"
              options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
              hint="Their working hours are set in this zone."
            />
          </div>

          <MultiSelectField
            label="Categories"
            required
            options={categoryOptions}
            value={draftCategories}
            onChange={setDraftCategories}
            hint="Students only see mentors in their own category. A mentor with none is unreachable."
            emptyMessage="No categories yet — create one first."
          />

          <TextField
            label="Reminder lead time"
            name="notification_lead_minutes"
            type="number"
            min={1}
            max={1440}
            defaultValue="15"
            hint="Minutes before a session to remind them. They can change this themselves."
          />

          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending}
            >
              Create mentor
            </Button>
          </div>
        </form>
      </Modal>

      {/* --------------------------------------------------------- edit */}
      <Modal
        open={editing !== null}
        onClose={closeForm}
        title={`Edit ${editing?.full_name ?? 'mentor'}`}
      >
        <form
          onSubmit={handleUpdate}
          className={styles.form}
          key={editing?.user_id}
        >
          <TextField
            label="Full name"
            name="full_name"
            defaultValue={editing?.full_name ?? ''}
            required
            maxLength={200}
          />

          <TextField
            label="Email"
            name="email"
            type="email"
            defaultValue={editing?.email ?? ''}
            required
            maxLength={320}
            hint="They sign in with this. Changing it moves their sign-in — tell them the new address, or they will be locked out."
          />

          <div className={styles.formRow}>
            <TextField
              label="Phone"
              name="phone"
              type="tel"
              defaultValue={editing?.phone ?? ''}
            />

            <SelectField
              label="Timezone"
              name="timezone"
              defaultValue={editing?.timezone ?? 'Asia/Kolkata'}
              options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
            />
          </div>

          <MultiSelectField
            label="Categories"
            required
            options={categoryOptions}
            value={draftCategories}
            onChange={setDraftCategories}
            hint="Removing every category hides this mentor from all students."
          />

          <TextField
            label="Reminder lead time"
            name="notification_lead_minutes"
            type="number"
            min={1}
            max={1440}
            defaultValue={String(editing?.notification_lead_minutes ?? 15)}
            hint="Minutes before a session. The mentor can override this."
          />

          {editing && !editing.profile_complete && (
            <Callout tone="warning">
              <p>
                This mentor has not completed their own profile, so students
                cannot book them yet. Only they can finish it — an admin cannot
                do it for them.
              </p>
            </Callout>
          )}

          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={updateMutation.isPending}
            >
              Save changes
            </Button>
          </div>
        </form>
      </Modal>

      <RatingOverrideDialog
        mentor={overriding}
        onClose={() => setOverriding(null)}
      />

      <ConfirmDialog
        open={pendingEmailChange !== null}
        title="Change their sign-in?"
        message={
          <>
            <strong>{pendingEmailChange?.mentor.full_name}</strong> signs in with{' '}
            <strong>{pendingEmailChange?.mentor.email}</strong>. After this they
            must use <strong>{pendingEmailChange?.to}</strong> instead.
            <br />
            <br />
            Their password does not change. Tell them the new address, or they
            will not be able to sign in.
          </>
        }
        confirmLabel="Change sign-in"
        destructive
        loading={updateMutation.isPending}
        onConfirm={() =>
          pendingEmailChange &&
          saveMentor(
            pendingEmailChange.mentor,
            pendingEmailChange.input,
            pendingEmailChange.to,
          )
        }
        onCancel={() => setPendingEmailChange(null)}
      />

      <ConfirmDialog
        open={resetting !== null}
        title="Reset this password?"
        message={
          <>
            A new temporary password will be generated for{' '}
            <strong>{resetting?.full_name}</strong>. Their current password stops
            working immediately, and any active sessions are signed out.
          </>
        }
        confirmLabel="Reset password"
        destructive
        loading={resetMutation.isPending}
        onConfirm={handleReset}
        onCancel={() => setResetting(null)}
      />

      {/*
        *** THE SESSION COUNT IS THE POINT OF THIS DIALOG ***
        Only shown when the server refused with a 409 because the mentor has
        confirmed future sessions. A mentor with none is deactivated on the
        first click and never reaches here.

        The number comes from the server, not from a count this page made:
        the admin is confirming the same figure the server will act on.
      */}
      <ConfirmDialog
        open={deactivating !== null}
        title="Cancel their upcoming sessions?"
        message={
          <>
            <strong>{deactivating?.mentor.full_name}</strong> has{' '}
            <strong>
              {deactivating?.sessions ?? 'some'} upcoming{' '}
              {deactivating?.sessions === 1 ? 'session' : 'sessions'}
            </strong>
            . Deactivating will cancel{' '}
            {deactivating?.sessions === 1 ? 'it' : 'them'} and notify those
            students.
            <br />
            <br />
            A deactivated mentor cannot sign in, so they could not attend
            {deactivating?.sessions === 1 ? ' it' : ' them'} anyway. Students
            on a plan get their {deactivating?.sessions === 1
              ? 'session'
              : 'sessions'}{' '}
            back.
          </>
        }
        confirmLabel="Deactivate and cancel"
        destructive
        loading={deactivate.isPending}
        onConfirm={() => void confirmDeactivate()}
        onCancel={() => setDeactivating(null)}
      />

      <CredentialDialog
        credential={credential}
        onClose={() => setCredential(null)}
      />
    </PageStack>
  )
}
